import { formatInTimeZone } from 'date-fns-tz';
import type {
  ExtractedReferenceGroup,
  HoraCertaImportRow,
  ReferenceEntityType,
  ReferenceGroupEntry,
} from 'contracts/HoraCertaImport';

type SpreadsheetValue = string | number | boolean | Date | null | undefined;
export type SpreadsheetRow = Record<string, SpreadsheetValue>;
type RequiredSpreadsheetField =
  | 'Agendamento'
  | 'Armazéns'
  | 'Transportadora'
  | 'Tomador'
  | 'Tipo Operação'
  | 'Dt. Agendamento'
  | 'Hr. Inicial'
  | 'Peso do Agendamento (Kg)'
  | 'Motorista'
  | 'CPF'
  | 'Placa Veículo'
  | 'Tipo Veículo'
  | 'Operação';
type SpreadsheetField = RequiredSpreadsheetField | 'Hr. Final';
type ReferenceField =
  'warehouse' | 'carrier' | 'company' | 'vehicleType' | 'operationType';

export interface HoraCertaSpreadsheetValidationIssue {
  row: number;
  field: SpreadsheetField;
  message: string;
}

export class HoraCertaSpreadsheetValidationError extends Error {
  constructor(public readonly issues: HoraCertaSpreadsheetValidationIssue[]) {
    const summary = `Foram encontrados ${issues.length} erro${
      issues.length === 1 ? '' : 's'
    } na planilha.`;
    const details = issues
      .map(
        ({ row, field, message }) =>
          `Linha ${row}, coluna "${field}": ${message}`
      )
      .join('; ');
    super(`${summary} ${details}`);
    Object.setPrototypeOf(this, HoraCertaSpreadsheetValidationError.prototype);
    this.name = 'HoraCertaSpreadsheetValidationError';
  }
}

class SpreadsheetCellError extends Error {
  constructor(public readonly issue: HoraCertaSpreadsheetValidationIssue) {
    super(`Linha ${issue.row}, coluna "${issue.field}" inválida.`);
    Object.setPrototypeOf(this, SpreadsheetCellError.prototype);
    this.name = 'SpreadsheetCellError';
  }
}

const requiredSpreadsheetFields: RequiredSpreadsheetField[] = [
  'Agendamento',
  'Armazéns',
  'Transportadora',
  'Tomador',
  'Tipo Operação',
  'Dt. Agendamento',
  'Peso do Agendamento (Kg)',
  'Motorista',
  'CPF',
  'Placa Veículo',
  'Tipo Veículo',
  'Operação',
];

const referenceFields: Array<{
  entityType: ExtractedReferenceGroup['entityType'];
  field: ReferenceField;
}> = [
  { entityType: 'warehouse', field: 'warehouse' },
  { entityType: 'carrier', field: 'carrier' },
  { entityType: 'vehicle_type', field: 'vehicleType' },
  { entityType: 'operation_type', field: 'operationType' },
  { entityType: 'company', field: 'company' },
];

const text = (value: SpreadsheetValue): string | null => {
  const normalized = String(value ?? '').trim();
  return normalized || null;
};

const invalidValue = (
  rowIndex: number,
  field: SpreadsheetField,
  message: string
): never => {
  throw new SpreadsheetCellError({ row: rowIndex + 3, field, message });
};

const invalidDateValue = (rowIndex: number, _value: unknown): never =>
  invalidValue(rowIndex, 'Dt. Agendamento', 'Data inválida');

const normalizeReference = (value: string): string =>
  value
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const formatDate = (
  year: number,
  month: number,
  day: number,
  rowIndex: number,
  source: unknown
): string => {
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return invalidDateValue(rowIndex, source);
  }

  return `${year.toString().padStart(4, '0')}-${month
    .toString()
    .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
};

const date = (value: SpreadsheetValue, rowIndex: number): string | null => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && !value.trim())
  ) {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return invalidDateValue(rowIndex, value);
    }

    return formatDate(
      value.getUTCFullYear(),
      value.getUTCMonth() + 1,
      value.getUTCDate(),
      rowIndex,
      value
    );
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) {
      return invalidDateValue(rowIndex, value);
    }

    const excelEpoch = Date.UTC(1899, 11, 30);
    const parsed = new Date(
      excelEpoch + Math.floor(value) * 24 * 60 * 60 * 1000
    );
    return formatDate(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth() + 1,
      parsed.getUTCDate(),
      rowIndex,
      value
    );
  }

  const raw = text(value);
  if (!raw) return null;

  if (/^\d+(?:[.,]\d+)?$/.test(raw)) {
    const serial = Number(raw.replace(',', '.'));
    if (Number.isFinite(serial) && serial > 0 && serial < 1000000) {
      return date(serial, rowIndex);
    }
  }

  const match = raw.match(
    /^(?:(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})|(\d{4})-(\d{1,2})-(\d{1,2}))(?:[ T].*)?$/
  );
  if (!match) return invalidDateValue(rowIndex, raw);

  let year = Number(match[3] || match[4]);
  let month = Number(match[2] || match[5]);
  let day = Number(match[1] || match[6]);
  if (match[3] && year < 100) year += 2000;

  if (match[3] && month > 12 && day <= 12) {
    [month, day] = [day, month];
  }

  return formatDate(year, month, day, rowIndex, raw);
};

export type HoraCertaRowImportStatus =
  | 'importable'
  | 'past'
  | 'cancelled'
  | 'quarantine'
  | 'invalid-date';

export const getHoraCertaRowImportStatus = (
  row: SpreadsheetRow,
  now = new Date(),
  pendingThirdOrders: ReadonlySet<string> = new Set()
): HoraCertaRowImportStatus => {
  if (normalizeReference(text(row.Status) || '') === 'CANCELADO') {
    return 'cancelled';
  }

  const thirdOrder = text(row.Agendamento)?.trim();
  if (thirdOrder && pendingThirdOrders.has(thirdOrder)) {
    return 'quarantine';
  }

  try {
    const scheduledDate = date(row['Dt. Agendamento'], 0);
    if (!scheduledDate) return 'invalid-date';
    const today = formatInTimeZone(now, 'America/Sao_Paulo', 'yyyy-MM-dd');
    return scheduledDate < today ? 'past' : 'importable';
  } catch {
    return 'invalid-date';
  }
};

const time = (
  value: SpreadsheetValue,
  rowIndex: number,
  header: 'Hr. Inicial' | 'Hr. Final'
): string | null => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return invalidValue(rowIndex, header, 'Hora inválida');
    }

    return `${value.getUTCHours().toString().padStart(2, '0')}:${value
      .getUTCMinutes()
      .toString()
      .padStart(2, '0')}`;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0 || value >= 1) {
      return invalidValue(rowIndex, header, 'Hora inválida');
    }

    const totalMinutes = Math.floor(value * 24 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  }

  const raw = text(value);
  if (!raw) return null;

  const match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) {
    return invalidValue(rowIndex, header, 'Hora inválida');
  }
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

const weight = (value: SpreadsheetValue, rowIndex: number): number | null => {
  const textValue = text(value);
  if (!textValue) return null;

  let normalized = textValue.replace(/\s/g, '');
  const lastComma = normalized.lastIndexOf(',');
  const lastDot = normalized.lastIndexOf('.');

  if (lastComma >= 0 && lastDot >= 0) {
    const decimalSeparator = lastComma > lastDot ? ',' : '.';
    const thousandsSeparator = decimalSeparator === ',' ? /\./g : /,/g;
    normalized = normalized
      .replace(thousandsSeparator, '')
      .replace(decimalSeparator, '.');
  } else if (lastComma >= 0) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(?:\.\d{3})+$/.test(normalized)) {
    normalized = normalized.replace(/\./g, '');
  }

  const result = Number(normalized);
  const cents = Math.round(result * 100);
  if (
    !Number.isFinite(result) ||
    result <= 0 ||
    Math.abs(result * 100 - cents) > 1e-6
  ) {
    return invalidValue(
      rowIndex,
      'Peso do Agendamento (Kg)',
      'Informe um peso positivo com até duas casas decimais'
    );
  }
  return cents / 100;
};

const captureSpreadsheetValue = <Value>(
  parse: () => Value,
  issues: HoraCertaSpreadsheetValidationIssue[]
): Value | null => {
  try {
    return parse();
  } catch (error) {
    if (!(error instanceof SpreadsheetCellError)) throw error;

    issues.push(error.issue);
    return null;
  }
};

export function toHoraCertaImportRows(
  rows: SpreadsheetRow[]
): HoraCertaImportRow[] {
  const issues: HoraCertaSpreadsheetValidationIssue[] = [];
  const importRows = rows.map((row, rowIndex) => {
    const status = text(row.Status);
    const isCancelled = normalizeReference(status || '') === 'CANCELADO';
    requiredSpreadsheetFields.forEach((field) => {
      if ((!isCancelled || field === 'Agendamento') && !text(row[field])) {
        issues.push({ row: rowIndex + 3, field, message: 'Campo obrigatório' });
      }
    });

    const tomador = text(row.Tomador);
    const scheduledDate = isCancelled
      ? null
      : captureSpreadsheetValue(
          () => date(row['Dt. Agendamento'], rowIndex),
          issues
        );
    const scheduledTime = isCancelled
      ? null
      : captureSpreadsheetValue(
          () => time(row['Hr. Inicial'], rowIndex, 'Hr. Inicial'),
          issues
        );
    const scheduledEndTime = isCancelled
      ? null
      : captureSpreadsheetValue(
          () => time(row['Hr. Final'], rowIndex, 'Hr. Final'),
          issues
        );
    const weightCapacity = isCancelled
      ? null
      : captureSpreadsheetValue(
          () => weight(row['Peso do Agendamento (Kg)'], rowIndex),
          issues
        );

    return {
      thirdOrder: text(row.Agendamento),
      warehouse: text(row.Armazéns),
      carrier: text(row.Transportadora),
      company: tomador,
      tomador,
      orderType: text(row['Tipo Operação']),
      scheduledDate,
      scheduledTime,
      scheduledEndTime,
      weightCapacity,
      driverName: text(row.Motorista),
      driverDocument: text(row.CPF),
      cellPhone: text(row.Celular),
      vehiclePlate: text(row['Placa Veículo']),
      vehicleType: text(row['Tipo Veículo']),
      observation: text(row.Observação) || null,
      operationType: text(row.Operação),
      status,
      cancellationReason: text(row.Motivo),
    };
  });

  if (issues.length) throw new HoraCertaSpreadsheetValidationError(issues);

  return importRows;
}

export function extractReferenceGroups(
  rows: HoraCertaImportRow[]
): ExtractedReferenceGroup[] {
  const activeRows = rows.filter(
    (row) => normalizeReference(row.status || '') !== 'CANCELADO'
  );
  if (activeRows.length === 0) return [];

  return referenceFields.map(({ entityType, field }) => {
    const groups = new Map<string, ReferenceGroupEntry>();

    activeRows.forEach((row) => {
      const externalValue = row[field];
      if (!externalValue) return;

      const normalizedValue = normalizeReference(externalValue);
      const entry = groups.get(normalizedValue);

      if (entry) {
        entry.occurrences += 1;
        entry.externalValues.push(externalValue);
        return;
      }

      groups.set(normalizedValue, {
        normalizedValue,
        externalValue,
        externalValues: [externalValue],
        occurrences: 1,
      });
    });

    const entries = Array.from(groups.values());
    return {
      entityType,
      values: entries.map((entry) => entry.externalValue),
      entries,
    };
  });
}

export const horaCertaReferenceEntityTypes: ReferenceEntityType[] = [
  'warehouse',
  'carrier',
  'company',
  'vehicle_type',
  'operation_type',
];
